/*
 * Purpose: Branded block ideas — BDNA, Squadbox, THRPY, SensAfffect until Director finds clear purpose
 * Last modified: 2026-06-27 | Agent
 * Completeness: 95
 */
import type { BrandedType } from '@/stores/workflowStore';
import type { LucideIcon } from 'lucide-react';
import { Dna, LayoutGrid, Mic, HeartPulse, Lock } from 'lucide-react';

export type BrandedBrand = 'BDNA' | 'Squadbox' | 'THRPY' | 'SensAfffect';

/** idea = catalog placeholder; ready = AI/Director may wire real behavior */
export type BrandedMaturity = 'idea' | 'ready';

export interface BrandedBlockStyle {
  bg: string;
  border: string;
  iconClass: string;
  badgeClass: string;
  stripe?: string;
}

export interface BrandedBlockDefinition {
  id: string;
  brand: BrandedBrand;
  subtype: BrandedType;
  label: string;
  shortLabel: string;
  description: string;
  icon: LucideIcon;
  style: BrandedBlockStyle;
  mega?: boolean;
  capabilities: string[];
  defaultConfig: Record<string, unknown>;
  /** All partner blocks ship as ideas until contextual configuration is clear */
  maturity: BrandedMaturity;
  /** Outcome keywords — Director may suggest this idea (not auto-build) */
  suggestWhen?: RegExp;
}

export const BRANDED_IDEA_HELP =
  'Branded blocks are ideas for now. AutoBlocks AI Director will build real wiring when contextual configuration states a clear purpose in this Mission.';

export const BDNA_LOCK_COLORS: Record<string, { label: string; stripe: string; border: string }> = {
  teal: { label: 'Teal lock', stripe: 'bg-teal-500', border: 'border-teal-500/50' },
  amber: { label: 'Amber lock', stripe: 'bg-amber-500', border: 'border-amber-500/50' },
  rose: { label: 'Rose lock', stripe: 'bg-rose-500', border: 'border-rose-500/50' },
  violet: { label: 'Violet lock', stripe: 'bg-violet-500', border: 'border-violet-500/50' },
};

export const BRANDED_BLOCKS: BrandedBlockDefinition[] = [
  {
    id: 'branded.bdna_lock',
    brand: 'BDNA',
    subtype: 'bdna_lock',
    label: 'BDNA Lock Block',
    shortLabel: 'BDNA Lock',
    description: 'Idea — colored identity lock. Director builds when purpose defines who may pass.',
    icon: Lock,
    maturity: 'idea',
    suggestWhen: /\b(bdna|identity|permission|lock|access|who can)\b/i,
    style: {
      bg: 'bg-teal-500/10',
      border: 'border-teal-500/50',
      iconClass: 'text-teal-600 dark:text-teal-400',
      badgeClass: 'bg-teal-500/20 text-teal-800 dark:text-teal-200',
      stripe: 'bg-teal-500',
    },
    capabilities: ['identity', 'permission', 'colored_lock'],
    defaultConfig: { lockColor: 'teal', bdnaProfile: '', buildStatus: 'idea' },
  },
  {
    id: 'branded.squadbox_mega',
    brand: 'Squadbox',
    subtype: 'squadbox_mega',
    label: 'Squadbox Mega Block',
    shortLabel: 'Squadbox Mega',
    description: 'Idea — squad orchestration mega block. Director builds when squad brief is clear.',
    icon: LayoutGrid,
    mega: true,
    maturity: 'idea',
    suggestWhen: /\b(squad|squadbox|team|multi.?agent|orchestrat)\b/i,
    style: {
      bg: 'bg-indigo-500/10',
      border: 'border-indigo-500/55',
      iconClass: 'text-indigo-600 dark:text-indigo-400',
      badgeClass: 'bg-indigo-500/20 text-indigo-900 dark:text-indigo-100',
    },
    capabilities: ['squad', 'mega', 'orchestration'],
    defaultConfig: { squadBrief: '', squadSize: 3, buildStatus: 'idea' },
  },
  {
    id: 'branded.thrpy_dictation',
    brand: 'THRPY',
    subtype: 'thrpy_dictation',
    label: 'THRPY Dictation Block',
    shortLabel: 'THRPY Dictation',
    description: 'Idea — therapy dictation capture. Director builds when clinical/personal purpose is set.',
    icon: Mic,
    maturity: 'idea',
    suggestWhen: /\b(thrpy|therapy|dictat|session notes|clinical notes|voice note)\b/i,
    style: {
      bg: 'bg-orange-500/10',
      border: 'border-orange-500/50',
      iconClass: 'text-orange-600 dark:text-orange-400',
      badgeClass: 'bg-orange-500/20 text-orange-900 dark:text-orange-100',
    },
    capabilities: ['dictation', 'speech', 'thrpy'],
    defaultConfig: { dictationMode: 'clinical', language: 'en', buildStatus: 'idea' },
  },
  {
    id: 'branded.sensaffect_emotion',
    brand: 'SensAfffect',
    subtype: 'sensaffect_emotion',
    label: 'SensAfffect Emotion Detection Block',
    shortLabel: 'SensAfffect Emotion',
    description: 'Idea — affect sensing. Director builds when sensitivity and output purpose are clear.',
    icon: HeartPulse,
    maturity: 'idea',
    suggestWhen: /\b(sensaffect|emotion|affect|mood|sentiment|feelings?)\b/i,
    style: {
      bg: 'bg-fuchsia-500/10',
      border: 'border-fuchsia-500/50',
      iconClass: 'text-fuchsia-600 dark:text-fuchsia-400',
      badgeClass: 'bg-fuchsia-500/20 text-fuchsia-900 dark:text-fuchsia-100',
    },
    capabilities: ['emotion', 'affect', 'sensing'],
    defaultConfig: { sensitivity: 'balanced', outputFormat: 'scores', buildStatus: 'idea' },
  },
];

export function getBrandedBlockDef(subtype: string): BrandedBlockDefinition | undefined {
  return BRANDED_BLOCKS.find((b) => b.subtype === subtype);
}

/** Purpose is clear enough for Director to treat as buildable (not just a map placeholder) */
export function brandedPurposeIsClear(subtype: string, config: Record<string, unknown>): boolean {
  const purpose = String(config.contextPurpose ?? '').trim();
  if (purpose.length >= 12) return true;

  switch (subtype) {
    case 'bdna_lock':
      return Boolean(String(config.bdnaProfile ?? '').trim());
    case 'squadbox_mega':
      return Boolean(String(config.squadBrief ?? '').trim());
    case 'thrpy_dictation':
      return purpose.length >= 6;
    case 'sensaffect_emotion':
      return purpose.length >= 6;
    default:
      return false;
  }
}

export function brandedBuildStatus(
  subtype: string,
  config: Record<string, unknown>
): BrandedMaturity {
  const def = getBrandedBlockDef(subtype);
  if (!def || def.maturity === 'idea') {
    return brandedPurposeIsClear(subtype, config) ? 'ready' : 'idea';
  }
  return def.maturity;
}

export interface BrandedBlockSuggestion {
  subtype: BrandedType;
  brand: BrandedBrand;
  label: string;
  reason: string;
}

/** Director hints — suggestions only; does not auto-place on map */
export function suggestBrandedBlockIdeas(outcome: string): BrandedBlockSuggestion[] {
  const text = outcome.trim();
  if (!text) return [];

  return BRANDED_BLOCKS.filter((b) => b.suggestWhen?.test(text)).map((b) => ({
    subtype: b.subtype,
    brand: b.brand,
    label: b.shortLabel,
    reason: `Your outcome may need ${b.shortLabel} — add to map when purpose is clear.`,
  }));
}

export function getBrandedBlockStyle(
  subtype: string,
  lockColor?: string
): BrandedBlockStyle & { icon: LucideIcon } {
  const def = getBrandedBlockDef(subtype);
  if (!def) {
    return {
      icon: Dna,
      bg: 'bg-muted/40',
      border: 'border-border',
      iconClass: 'ui-icon',
      badgeClass: 'bg-muted',
    };
  }

  if (subtype === 'bdna_lock') {
    const key = lockColor && BDNA_LOCK_COLORS[lockColor] ? lockColor : 'teal';
    const c = BDNA_LOCK_COLORS[key];
    const tone: Record<string, BrandedBlockStyle & { icon: LucideIcon }> = {
      teal: {
        icon: Lock,
        bg: 'bg-teal-500/10',
        border: c.border,
        iconClass: 'text-teal-600 dark:text-teal-400',
        badgeClass: 'bg-teal-500/20 text-teal-800 dark:text-teal-200',
        stripe: c.stripe,
      },
      amber: {
        icon: Lock,
        bg: 'bg-amber-500/10',
        border: c.border,
        iconClass: 'text-amber-600 dark:text-amber-400',
        badgeClass: 'bg-amber-500/20 text-amber-900 dark:text-amber-100',
        stripe: c.stripe,
      },
      rose: {
        icon: Lock,
        bg: 'bg-rose-500/10',
        border: c.border,
        iconClass: 'text-rose-600 dark:text-rose-400',
        badgeClass: 'bg-rose-500/20 text-rose-900 dark:text-rose-100',
        stripe: c.stripe,
      },
      violet: {
        icon: Lock,
        bg: 'bg-violet-500/10',
        border: c.border,
        iconClass: 'text-violet-600 dark:text-violet-400',
        badgeClass: 'bg-violet-500/20 text-violet-900 dark:text-violet-100',
        stripe: c.stripe,
      },
    };
    return tone[key] ?? tone.teal;
  }

  return { icon: def.icon, ...def.style };
}

export function isMegaBrandedBlock(subtype: string): boolean {
  return Boolean(getBrandedBlockDef(subtype)?.mega);
}
